// src/componentes/RegistrarPago/RegistrarPago.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/firebaseconfig';
import { processPayment } from '../../services/deudaServices';
import notify from "../common/notify";
import './RegistrarPago.css';
import { useUserContext } from '../GestionDeUsuarios/UserContext';
import AccessDenied from '../Auth/AccessDenied';
import { registrarAuditoria } from '../../helpers/auditoria';
import { METODOS_PAGO, normalizeMetodoPago } from '../../helpers/metodosPago';
import { calcularPendienteDevolver } from '../../helpers/devolucion';
import { procesarDevengoComision } from '../../services/procesarDevengoComision';

const RegistrarPago = () => {
  const { id: clienteId, deudaId } = useParams();
  const navigate = useNavigate();
  const { can } = useUserContext();

  const [amount, setAmount] = useState('');
  const [metodo, setMetodo] = useState('efectivo');
  const [referencia, setReferencia] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detectar si la deuda está en devolución para firmar el monto en negativo
  const [esDevolucion, setEsDevolucion] = useState(false);
  const [deuda, setDeuda] = useState(null);
  const [loadingDeuda, setLoadingDeuda] = useState(true);

  useEffect(() => {
    const loadDeuda = async () => {
      try {
        const snap = await getDoc(doc(db, 'deudas', deudaId));
        if (snap.exists()) {
          const deudaData = { id: snap.id, ...snap.data() };
          setDeuda(deudaData);
          const enDev = !!deudaData?.devolucion || deudaData?.estado === 'en_devolucion';
          setEsDevolucion(enDev);

          // UX-2 (2026-06-17): pre-llenar el monto sugerido para que el cobrador
          // no teclee de cero. Pago normal → mensualidad pactada, topada al saldo
          // restante (no puede pagar más de lo que debe). Devolución → pendiente.
          if (enDev) {
            const pend = Number(calcularPendienteDevolver(deudaData) || 0);
            if (pend > 0) setAmount(String(pend));
          } else {
            const saldo = Number(deudaData?.saldoRestante ?? deudaData?.saldo ?? 0);
            const mensualidad = Number(deudaData?.mensualidad ?? deudaData?.pagoMensual ?? 0);
            const sugerido = mensualidad > 0 ? Math.min(mensualidad, saldo) : saldo;
            if (sugerido > 0) setAmount(String(Math.round(sugerido * 100) / 100));
          }
        }
      } catch (e) {
        console.error('Error cargando deuda:', e);
      } finally {
        setLoadingDeuda(false);
      }
    };
    loadDeuda();
  }, [deudaId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!can('deudas.registrarPago')) return;
    if (isSubmitting) return;

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      notify.error('Ingresa un monto válido');
      return;
    }

    // Firmar el monto según el modo actual
    const signedCantidad = esDevolucion ? -Math.abs(parsed) : Math.abs(parsed);
    const estado = String(deuda?.estado || deuda?.estatus || '').toLowerCase();
    const pendienteDevolucion = esDevolucion ? calcularPendienteDevolver(deuda) : 0;

    if (estado === 'cancelada' || estado === 'cancelado') {
      notify.error('Esta deuda esta cancelada y no acepta movimientos.');
      return;
    }

    if (estado === 'devuelta' || estado === 'cerrada_devolucion') {
      notify.error('Esta devolucion ya esta cerrada.');
      return;
    }

    // v1.2: priorizar saldoRestante sobre campo estado legacy.
    // Solo bloquea si saldo<=0 AND estado=liquidada, o si activo===false.
    const saldoRestante = Number(deuda?.saldoRestante ?? deuda?.saldo ?? 0);
    if (!esDevolucion && saldoRestante <= 0 && estado === 'liquidada') {
      notify.error('Esta deuda ya esta liquidada (saldo cero).');
      return;
    }
    if (!esDevolucion && deuda?.activo === false) {
      notify.error('Esta deuda esta inactiva. Reactivala antes de registrar movimientos.');
      return;
    }

    if (esDevolucion && parsed > pendienteDevolucion) {
      notify.error(`El monto excede el pendiente por devolver ($${pendienteDevolucion.toLocaleString('es-MX')}).`);
      return;
    }

    const metodoInfo = normalizeMetodoPago(metodo);
    const referenciaLimpia = referencia.trim();

    if (metodoInfo.transferencia && !referenciaLimpia) {
      notify.error('Captura la referencia de la transferencia o deposito.');
      return;
    }

    setIsSubmitting(true);
    try {
      await processPayment(deudaId, signedCantidad, metodo, {
        referencia: referenciaLimpia,
      });
      await registrarAuditoria({
        accion: esDevolucion ? 'deudas.registrarDevolucionPago' : 'deudas.registrarPago',
        entidadTipo: 'deuda',
        entidadId: deudaId,
        despues: { cantidad: signedCantidad, metodo: metodoInfo.value, metodoEtiqueta: metodoInfo.label, referencia: referenciaLimpia },
      });

      // ── Devengo automático de comisiones (solo en pagos positivos) ──────
      // EST-A.1 patron: await + try/catch + notify para que el operador
      // se entere si la creacion de CxP just-in-time falla. Si no, el hito
      // queda sin devengar y nadie se da cuenta (igual que el bug que cacho
      // ModalPagoSemimasivo en sesion previa).
      if (!esDevolucion) {
        try {
          const { devengados, errores } = await procesarDevengoComision(deudaId);
          if (devengados > 0) {
            notify.success(
              `${devengados} hito${devengados > 1 ? 's' : ''} de comisión devengado${devengados > 1 ? 's' : ''} y CxP generada${devengados > 1 ? 's' : ''}.`
            );
          }
          if (errores.length > 0) {
            console.error('procesarDevengoComision errores:', errores);
            notify.error(
              `Pago registrado, pero el devengo de comisiones reportó errores: ${errores.slice(0, 2).join(' | ')}`
            );
          }
        } catch (e) {
          console.error('procesarDevengoComision fatal:', e);
          notify.error(
            `Pago registrado, pero el devengo falló: ${e?.message || 'error'}. Revisa manualmente.`
          );
        }
      }

      notify.success('Movimiento registrado correctamente');
      navigate(`/app/cliente/${clienteId}/deudas/${deudaId}`);
    } catch (error) {
      console.error('Error al registrar pago:', error);
      notify.error('No se pudo completar el registro.');
      setIsSubmitting(false);
    }
  };

  if (!can('deudas.registrarPago')) {
    return <AccessDenied message="No tienes permiso para registrar pagos." />;
  }

  return (
    <div className="registrar-pago-container">
      <h2>{esDevolucion ? 'Registrar Devolución' : 'Registrar Pago'}</h2>

      {loadingDeuda ? (
        <p>Cargando…</p>
      ) : (
        <form onSubmit={handleSubmit} className="form-pago">
          {esDevolucion ? (
            <div className="hint">
              Pendiente por devolver: <strong>${calcularPendienteDevolver(deuda).toLocaleString('es-MX')}</strong>
            </div>
          ) : (
            <div className="hint">
              Mensualidad: <strong>${Number(deuda?.mensualidad ?? deuda?.pagoMensual ?? 0).toLocaleString('es-MX')}</strong>
              {"  ·  "}
              Saldo: <strong>${Number(deuda?.saldoRestante ?? deuda?.saldo ?? 0).toLocaleString('es-MX')}</strong>
              <span style={{ color: "#64748b" }}> — sugerido pre-llenado, ajústalo si aplica</span>
            </div>
          )}
          <label>
            {esDevolucion ? 'Monto a devolver:' : 'Monto a pagar:'}
            <input
              type="number"
              step="any"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              disabled={isSubmitting}
              placeholder={esDevolucion ? 'Se registrará como salida (negativo)' : ''}
            />
          </label>

          <label>
            Método de {esDevolucion ? 'devolución' : 'pago'}:
            <select
              value={metodo}
              onChange={(e) => setMetodo(e.target.value)}
              disabled={isSubmitting}
            >
              {METODOS_PAGO.filter((item) => item.value !== 'historico').map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>

          <label>
            Referencia:
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              disabled={isSubmitting}
              placeholder={normalizeMetodoPago(metodo).transferencia ? 'Banco, folio o comprobante' : 'Opcional'}
              required={normalizeMetodoPago(metodo).transferencia}
            />
          </label>

          {esDevolucion && (
            <p className="hint">
              Este movimiento se guardará como <strong>negativo</strong> para que
              aparezca correctamente en el estado de cuenta.
            </p>
          )}

          <div className="botones-pago">
            <button type="submit" disabled={isSubmitting} className="btn-registrar">
              {isSubmitting ? 'Guardando…' : 'Guardar'}
            </button>
            <button
              type="button"
              className="btn-cancelar"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RegistrarPago;
